"use client"

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import ReportService from '@/api/reports.service';
import {
  ArrowLeft,
  BarChart2,
  Building2,
  Calculator,
  Calendar,
  CreditCard,
  Download,
  FileSpreadsheet,
  FilePieChart,
  FileText,
  DollarSign,
  Loader2,
  FilePlus2,
  Users,
  Clock,
  User
} from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface ReportDetailProps {
  publicId: string;
}

export default function Report({ publicId }: ReportDetailProps) {
  const [report, setReport] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    fetchReport();
  }, [publicId]);

  const fetchReport = async () => {
    if (!publicId) return;

    setIsLoading(true);
    try {
      const response = await ReportService.getReport(publicId);
      
      if (response.data.result === 'success') {
        setReport(response.data.report);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to load report details",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      toast({
        title: "Error",
        description: "Failed to load report details",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getReportTypeIcon = (type: string, size: string = "w-5 h-5") => {
    if (!type) return <FileText className={size} />;

    // Financial report types
    if (type.includes('income_statement')) {
      return <DollarSign className={`${size} text-blue-500`} />;
    } else if (type.includes('balance_sheet')) {
      return <FileText className={`${size} text-green-500`} />;
    } else if (type.includes('cash_flow')) {
      return <CreditCard className={`${size} text-amber-500`} />;
    } else if (type.includes('changes_in_equity')) {
      return <BarChart2 className={`${size} text-purple-500`} />;
    } 
    // Property report types
    else if (type.includes('rental_income')) {
      return <Building2 className={`${size} text-blue-500`} />;
    } else if (type.includes('occupancy') || type.includes('vacancy')) {
      return <Building2 className={`${size} text-green-500`} />;
    } 
    // Tenant report types
    else if (type.includes('tenant_ledger')) {
      return <FileSpreadsheet className={`${size} text-blue-500`} />;
    } else if (type.includes('aging_debtors')) {
      return <Calculator className={`${size} text-red-500`} />;
    } 
    // Maintenance report types
    else if (type.includes('maintenance') || type.includes('repairs')) {
      return <FilePlus2 className={`${size} text-amber-500`} />;
    } else if (type.includes('capex')) {
      return <FilePlus2 className={`${size} text-green-500`} />;
    } 
    // Loan report types
    else if (type.includes('amortization')) {
      return <FilePieChart className={`${size} text-purple-500`} />;
    } else {
      return <FileText className={size} />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'draft':
        return <Badge variant="outline" className="bg-gray-100 text-gray-800 border-gray-200">Draft</Badge>;
      case 'generated':
        return <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">Generated</Badge>;
      case 'archived':
        return <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200">Archived</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const handleExportPdf = async () => {
    try {
      const response = await ReportService.exportReportToPdf(publicId);
      console.log("exportReportToPdf response.data", response.data);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.name}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast({
        title: "Error",
        description: "Failed to download PDF",
        variant: "destructive",
      });
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await ReportService.exportReportToExcel(publicId);
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.name}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading Excel:", error);
      toast({
        title: "Error",
        description: "Failed to download Excel file",
        variant: "destructive",
      });
    }
  };

  const handleGenerateReport = async () => {
    try {
      const response = await ReportService.generateReport(publicId);
      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: "Report generated successfully",
        });
        fetchReport();
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to generate report",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error generating report:", error);
      toast({
        title: "Error",
        description: "An error occurred while generating the report",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-[70vh]">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Loading report details...</p>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="flex flex-col justify-center items-center h-[70vh]">
        <p className="text-muted-foreground">Report not found</p>
        <Button onClick={() => navigate('/reports')} variant="outline" className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Reports
        </Button>
      </div>
    );
  }

  return (
    <div className="container py-8 mx-auto max-w-7xl">
      <div className="flex flex-col space-y-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => navigate('/reports')} 
              variant="ghost" 
              size="sm"
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-2xl font-bold tracking-tight">
              {report.name}
            </h1>
            <div className="ml-2">
              {getStatusBadge(report.status)}
            </div>
          </div>
          
          <div className="flex gap-2">
            {report.status === 'draft' && (
              <Button 
                onClick={handleGenerateReport}
                className="gap-1"
              >
                <BarChart2 className="w-4 h-4" />
                Generate Report
              </Button>
            )}
            
            {report.status === 'generated' && (
              <>
                <Button 
                  variant="outline" 
                  onClick={handleExportPdf}
                  className="gap-1"
                >
                  <Download className="w-4 h-4" />
                  Export PDF
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleExportExcel}
                  className="gap-1"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Export Excel
                </Button>
              </>
            )}
          </div>
        </div>
        
        {/* Main Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Column - Report Overview */}
          <Card className="md:col-span-1 shadow-sm border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart2 className="w-5 h-5 text-primary" />
                Report Overview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  {getReportTypeIcon(report.reportType)}
                  <div>
                    <p className="text-sm font-medium">Report Type</p>
                    <p className="text-sm text-muted-foreground">
                      {report.reportType ? report.reportType.replace(/_/g, ' ').split(' ').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'N/A'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Date Range</p>
                    <p className="text-sm text-muted-foreground">
                      {report.dateRange === 'custom' ? (
                        <>
                          {format(new Date(report.startDate), "MMM d, yyyy")} - {format(new Date(report.endDate), "MMM d, yyyy")}
                        </>
                      ) : (
                        report.dateRange?.charAt(0).toUpperCase() + report.dateRange?.slice(1)
                      )}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Created By</p>
                    <p className="text-sm text-muted-foreground">{report.createdByName || 'Unknown'}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Created On</p>
                    <p className="text-sm text-muted-foreground">
                      {report.dateCreated && format(new Date(report.dateCreated), "MMM d, yyyy")}
                    </p>
                  </div>
                </div>
              </div>
              
              <Separator />
              
              {/* Description */}
              <div className="space-y-2">
                <p className="text-sm font-medium">Description</p>
                <p className="text-sm text-muted-foreground">
                  {report.description || 'No description provided'}
                </p>
              </div>
            </CardContent>
          </Card>
          
          {/* Right Column - Report Content */}
          <div className="md:col-span-2 space-y-6">
            <Tabs defaultValue="report" className="w-full">
              <TabsList className="grid grid-cols-2 w-full md:w-[400px]">
                <TabsTrigger value="report">Report Content</TabsTrigger>
                <TabsTrigger value="preview">
                  {report.status === 'generated' ? 'Preview' : 'Settings'}
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="report" className="mt-4 space-y-4">
                {report.status === 'generated' ? (
                  <Card className="shadow-sm border-slate-200 dark:border-slate-800">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">
                        {report.reportType.replace(/_/g, ' ').split(' ').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                      </CardTitle>
                      <CardDescription>
                        Generated on {format(new Date(report.dateUpdated || report.dateCreated), "MMM d, yyyy 'at' h:mm a")}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {/* We would render the report content here based on its type */}
                      {report.reportSections && report.reportSections.length > 0 ? (
                        <div className="space-y-6">
                          {report.reportSections.map((section: any, index: number) => (
                            <div key={section.publicId || index} className="space-y-2">
                              {section.title && (
                                <h3 className="text-lg font-medium">{section.title}</h3>
                              )}
                              {section.content && typeof section.content === 'object' ? (
                                <pre className="bg-muted p-4 rounded-md overflow-x-auto">
                                  {JSON.stringify(section.content, null, 2)}
                                </pre>
                              ) : (
                                <div className="text-muted-foreground">
                                  {section.content || 'No content available'}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <BarChart2 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                          <h3 className="text-lg font-medium mb-2">No Report Sections</h3>
                          <p className="text-muted-foreground mb-6">
                            This report has no content sections defined.
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="shadow-sm border-slate-200 dark:border-slate-800">
                    <CardContent className="pt-6">
                      <div className="text-center py-8">
                        <BarChart2 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                        <h3 className="text-lg font-medium mb-2">Draft Report</h3>
                        <p className="text-muted-foreground mb-6">
                          This report is in draft status. Generate the report to view its content.
                        </p>
                        <Button onClick={handleGenerateReport}>
                          <BarChart2 className="w-4 h-4 mr-2" />
                          Generate Report
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
              
              <TabsContent value="preview" className="mt-4">
                <Card className="shadow-sm border-slate-200 dark:border-slate-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">
                      {report.status === 'generated' ? 'Report Preview' : 'Report Settings'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {report.status === 'generated' ? (
                      <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                          Preview is not available in this view. Please export the report to view its contents.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <Button 
                            variant="outline" 
                            onClick={handleExportPdf}
                            className="gap-1"
                          >
                            <Download className="w-4 h-4" />
                            Export as PDF
                          </Button>
                          <Button 
                            variant="outline" 
                            onClick={handleExportExcel}
                            className="gap-1"
                          >
                            <FileSpreadsheet className="w-4 h-4" />
                            Export as Excel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 gap-4">
                          <div className="space-y-2">
                            <p className="text-sm font-medium">Report Period</p>
                            <p className="text-muted-foreground text-sm">
                              {report.dateRange === 'custom' ? (
                                <>
                                  {format(new Date(report.startDate), "MMM d, yyyy")} - {format(new Date(report.endDate), "MMM d, yyyy")}
                                </>
                              ) : (
                                report.dateRange?.charAt(0).toUpperCase() + report.dateRange?.slice(1)
                              )}
                            </p>
                          </div>
                          
                          <div className="space-y-2">
                            <p className="text-sm font-medium">Report Type</p>
                            <p className="text-muted-foreground text-sm">
                              {report.reportType ? report.reportType.replace(/_/g, ' ').split(' ').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'N/A'}
                            </p>
                          </div>
                          
                          {/* For property reports */}
                          {report.propertyIds && (
                            <div className="space-y-2">
                              <p className="text-sm font-medium">Properties Included</p>
                              <p className="text-muted-foreground text-sm">
                                {report.propertyIds.length} properties selected
                              </p>
                            </div>
                          )}
                          
                          {/* For tenant reports */}
                          {report.tenantIds && (
                            <div className="space-y-2">
                              <p className="text-sm font-medium">Tenants Included</p>
                              <p className="text-muted-foreground text-sm">
                                {report.tenantIds.length} tenants selected
                              </p>
                            </div>
                          )}
                          
                          {/* For maintenance reports */}
                          {report.categoryIds && (
                            <div className="space-y-2">
                              <p className="text-sm font-medium">Categories Included</p>
                              <p className="text-muted-foreground text-sm">
                                {report.categoryIds.length} categories selected
                              </p>
                            </div>
                          )}
                          
                          {/* For loan reports */}
                          {report.loanAmount && (
                            <div className="space-y-2">
                              <p className="text-sm font-medium">Loan Details</p>
                              <div className="text-muted-foreground text-sm">
                                <p>Amount: ${report.loanAmount}</p>
                                <p>Rate: {report.interestRate}%</p>
                                <p>Term: {report.loanTerm} months</p>
                                <p>Frequency: {report.paymentFrequency}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
} 